import { useState, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { useElementHeightObserver } from '../../../hooks/use-element-height-observer'
import { SIDEPANEL_LAYER_Z } from '../../../constants'

interface SidepanelFooterSlotProps {
  /**
   * Explicit box height, which hands the slot to `overlay`. Omit to size to
   * `children` — the chat input's own resting height, which grows as it fills.
   */
  height?: number
  /**
   * Take {@link height} as given rather than transitioning to it — something is
   * already driving it frame by frame (the Notepad's top edge under a drag),
   * and easing on top of that reads as the box lagging the pointer.
   */
  instant?: boolean
  /** The resting occupant: the chat input. */
  children: ReactNode
  /** Surface that takes the box over while something has claimed it (the Notepad). */
  overlay?: ReactNode
}

/**
 * The footer's bottom slot, and the one owner of its height.
 *
 * The chat input and the Meeting View's Notepad (HIG-5829) are different
 * surfaces, so swapping them outright would pop from one height to the other.
 * Instead the box transitions between them and the overlay fills it, which
 * reads as the chat input growing into the notepad and settling back down.
 *
 * The chat input stays mounted underneath rather than unmounting: it carries
 * the panel's window-scoped effects (screen-context sources, push-to-talk
 * dictation), and those shouldn't blink out because a notepad is open.
 */
export function SidepanelFooterSlot({ height, instant, children, overlay }: SidepanelFooterSlotProps) {
  const [restingHeight, setRestingHeight] = useState<number>()
  const measureRef = useElementHeightObserver(setRestingHeight)
  const isOverlaid = height !== undefined

  return (
    <div
      className={twMerge(
        'pointer-events-auto relative',
        // Withheld until the first measurement lands, so the box doesn't animate
        // up from nothing on the panel's very first paint.
        restingHeight !== undefined &&
          !instant &&
          'transition-[height] duration-[250ms] ease-[cubic-bezier(0.32,0.72,0,1)]',
      )}
      // Top of the panel's paint order: the composer stays visible and usable
      // with a View's bottom sheet open, so the sheet slides up *behind* it
      // rather than stopping short and leaving a seam around the input's card.
      style={{ height: height ?? restingHeight, zIndex: SIDEPANEL_LAYER_Z.chatInput }}
    >
      {/* Bottom-anchored and out of flow, so it keeps reporting its own resting
          height while the box is transitioning to someone else's. flex, so the
          chat input's inset bottom margin can't collapse out of that
          measurement and leave the box short by it.

          `invisible` while overlaid rather than unmounted or `hidden`: it still
          lays out (so the resting height stays live for the trip back, which is
          what the collapse animates to) but leaves the tab order and the
          accessibility tree, which a control hidden behind an opaque panel has
          no business being in. Its stale raster is the overlay's problem to
          cover — see the note below. */}
      <div ref={measureRef} className={twMerge('absolute inset-x-0 bottom-0 flex flex-col', isOverlaid && 'invisible')}>
        {children}
      </div>
      {/* The slot places the overlay rather than trusting it to place itself —
          filling the box is what makes the height transition read as a morph.

          Above the chat input's own `z-10`, but only while it holds the box.
          `invisible` stops the input painting, yet Chromium can leave that
          box's last raster on screen for ~150ms while the height transition
          re-rasters the region — long enough to see the context pill's
          `icon://` app icon stranded there as a grey placeholder square (it
          repaints as the not-yet-decoded placeholder, then never repaints,
          because by then it's hidden). Painting over it covers whatever the
          compositor is slow to drop.

          And inert the rest of the time: the wrapper spans the whole slot, so
          left claiming it on top of the chat input it is a sheet over every
          control in it. */}
      <div className={twMerge('absolute inset-0', isOverlaid ? 'z-20' : 'pointer-events-none')}>{overlay}</div>
    </div>
  )
}
